import React from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Button,
  VStack,
  Flex,
  Image,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { useRecoilState } from 'recoil';
import { getFullAccessModalOpenStatus } from '../../atoms/rootAtom';
import { useNavigate } from 'react-router-dom';
import { pxToRem } from '../../utils/editor';
import { PaymentService } from '../../services/paymentService';
import { pricingPlans, priceDesc, freePlanDesc } from '../../utils/const';
import { useTranslation } from 'react-i18next';

const GetFullAccessModal: React.FC = () => {
  const [isOpen, setIsOpen] = useRecoilState(getFullAccessModalOpenStatus);
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation();
  const textColor = useColorModeValue('#333333', 'white');

  // New states for free trial
  const [isLoading, setIsLoading] = React.useState(false);
  const [freeTrialStarted, setFreeTrialStarted] = React.useState(false);
  const [showFreeTrialSection, setShowFreeTrialSection] = React.useState(true);

  // Free trial logic
  const startFreeTrial = async () => {
    setIsLoading(true);

    try {
      const result = await PaymentService.startFreeTrial();

      if (result?.status === "success") {
        setFreeTrialStarted(true);

        toast({
          title: "Free Trial Started",
          description: result?.message || "Your free trial has been activated",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        // Redirect to /edit after success
        setTimeout(() => {
          navigate("/edit");
          handleClose();
        }, 1000);
      } else {
        toast({
          title: "Free Trial Error",
          description:
            result?.message || "Failed to start free trial. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error: any) {
      console.error("Failed to start free trial:", error);
      const msg = error?.response?.data?.message;
      toast({
        title: "Free Trial Error",
        description: msg || "Failed to start free trial. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const checkTrialStatus = async () => {
    try {
      const result = await PaymentService.checkFreeTrialStatus();

      if (result?.trial_status === "used") {
        setShowFreeTrialSection(false);
      } else if (result?.trial_status === "in_trial") {
        setFreeTrialStarted(true);
      }
    } catch (error) {
      console.error("Failed to check free trial status:", error);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      checkTrialStatus();
    }
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handlePlanSelect = (plan: string) => {
    navigate(`/payment-current?planType=${plan}`);
    handleClose();
  };

  const handleSelectFreePlan = async () => {
    try {
      await PaymentService.selectFreePlan();
    } catch (error) {
      console.error('Failed to select free plan:', error);
    }
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="xl">
      <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
      <ModalContent
        maxW={pxToRem(1500)}
        maxH="90vh"
        borderRadius={pxToRem(20)}
        p={pxToRem(32)}
        border="1px solid"
        borderColor="#1D8AFF"
        overflowY="auto"
      >
        <ModalCloseButton
          _focus={{
            boxShadow: 'none',
            outline: 'none'
          }}
        />
        <ModalBody p={0}>
          <Flex direction="column" mx="auto" px={{ base: pxToRem(4), md: 0 }}>
            {/* Top Section */}
            <Box mb={pxToRem(40)}>
              <Flex
                direction={{ base: "column", md: "row" }}
                align="stretch"
                justify="space-between"
              >
                <Flex
                  w={showFreeTrialSection ? pxToRem(463) : "100%"}
                  align="center"
                >
                  <Text
                    fontSize={pxToRem(22)}
                    fontWeight="bold"
                    color={textColor}
                    lineHeight={pxToRem(32)}
                    mb={0}
                  >
                    From intelligent PDF and CSV reading and analysis to seamless writing, editing, and typesetting, we streamline your journey from ideas to polished results.
                  </Text>
                </Flex>
                {showFreeTrialSection && (
                  <Box
                    bg="url(/static/payment/free-bg.png)"
                    backgroundSize={"100% 100%"}
                    backgroundRepeat={"no-repeat"}
                    borderRadius={pxToRem(20)}
                    p={pxToRem(20)}
                    mt={{ base: pxToRem(20), md: 0 }}
                    w={pxToRem(914)}
                    minH={pxToRem(204)}
                    textAlign={!freeTrialStarted ? "center" : "left"}
                  >
                    <Text
                      fontSize={pxToRem(32)}
                      fontWeight="700"
                      mb={pxToRem(16)}
                    >
                      {!freeTrialStarted
                        ? "Start a 7-day Professional Plan free trial"
                        : "Doenba Edit Professional"}
                      {freeTrialStarted ? (
                        <Box
                          display="inline-block"
                          bg="rgba(255, 255, 255, 0.35)"
                          borderRadius={pxToRem(20)}
                          px={pxToRem(24)}
                          py={pxToRem(2)}
                          mb={pxToRem(16)}
                          ml={pxToRem(34)}
                          border={"1px solid #FFF"}
                        >
                          <Text
                            fontSize={pxToRem(16)}
                            fontWeight="600"
                            color="#333"
                            mb={0}
                          >
                            7-day free trial
                          </Text>
                        </Box>
                      ) : null}
                    </Text>

                    <Text fontSize={pxToRem(24)} color={"#333"} mb={pxToRem(16)}>
                      Get full access to all Doenba Edit features.
                    </Text>
                    {!freeTrialStarted ? (
                      <Button
                        bg="#333333"
                        color="white"
                        fontSize={pxToRem(24)}
                        borderRadius={pxToRem(12)}
                        fontWeight="700"
                        px={pxToRem(48)}
                        py={pxToRem(14)}
                        _hover={{
                          bg: "#555555",
                        }}
                        transition="all 0.2s"
                        onClick={startFreeTrial}
                        isLoading={isLoading}
                        loadingText="Starting..."
                      >
                        Start free trial
                      </Button>
                    ) : null}
                  </Box>
                )}
              </Flex>
            </Box>

            {/* Plan Section - Free Plan & Professional Plan side by side */}
            <Flex
              gap={pxToRem(24)}
              direction={{ base: "column", md: "row" }}
              align="stretch"
            >
              {/* Free Plan - Left Half */}
              <Flex
                bg="#F8FAFB"
                borderRadius={pxToRem(20)}
                px={pxToRem(40)}
                py={pxToRem(28)}
                boxShadow="0px 2px 12px rgba(0, 0, 0, 0.06)"
                flex="1"
                borderTop="3px solid #48BB78"
                position="relative"
                overflow="hidden"
                direction="column"
              >
                <Flex align="center" mb={pxToRem(20)}>
                  <Flex
                    w={pxToRem(50)}
                    h={pxToRem(50)}
                    bg="linear-gradient(135deg, #48BB78, #38A169)"
                    borderRadius={pxToRem(12)}
                    mr={pxToRem(14)}
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                      <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M14 2V8H20" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M9 15L11 17L15 13" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </Flex>
                  <Box>
                    <Text
                      fontSize={pxToRem(26)}
                      fontWeight="700"
                      color={textColor}
                      mb={pxToRem(4)}
                    >
                      {t('free-plan-title')}
                    </Text>
                    <Text
                      fontSize={pxToRem(14)}
                      fontWeight="600"
                      color="#48BB78"
                      mb={0}
                    >
                      {t('free-plan-subtitle')}
                    </Text>
                  </Box>
                </Flex>

                <VStack spacing={pxToRem(10)} align="flex-start" flex="1">
                  {freePlanDesc.map((feature, index) => (
                    <Flex key={index} align="center">
                      <Flex
                        w={pxToRem(20)}
                        h={pxToRem(20)}
                        bg="#48BB78"
                        borderRadius="50%"
                        mr={pxToRem(14)}
                        flexShrink={0}
                        align="center"
                        justify="center"
                      >
                        <Text fontSize={pxToRem(12)} color="white" mb={0} lineHeight="1">
                          &#10003;
                        </Text>
                      </Flex>
                      <Text fontSize={pxToRem(16)} color="#555" mb={0}>
                        {t(feature)}
                      </Text>
                    </Flex>
                  ))}
                </VStack>

                <Button
                  bg="linear-gradient(135deg, #48BB78, #38A169)"
                  color="white"
                  borderRadius={pxToRem(10)}
                  w={pxToRem(140)}
                  h={pxToRem(40)}
                  fontSize={pxToRem(16)}
                  fontWeight="700"
                  mt="auto"
                  alignSelf="center"
                  _hover={{
                    opacity: 0.9,
                    transform: "translateY(-2px)",
                  }}
                  transition="all 0.2s"
                  onClick={handleSelectFreePlan}
                >
                  Select
                </Button>
              </Flex>

              {/* Professional Plan + Pricing - Right Half */}
              <Box
                bg="linear-gradient(135deg, #F0F7FF 0%, #E8F2FF 100%)"
                borderRadius={pxToRem(20)}
                px={pxToRem(40)}
                py={pxToRem(28)}
                boxShadow="0px 4px 20px rgba(29, 138, 255, 0.12)"
                flex="1"
                borderTop="3px solid #1D8AFF"
                position="relative"
                overflow="hidden"
              >
                {/* Recommended Badge */}
                <Box
                  position="absolute"
                  top={pxToRem(16)}
                  right={pxToRem(20)}
                  bg="linear-gradient(135deg, #1D8AFF, #0066CC)"
                  color="white"
                  fontSize={pxToRem(11)}
                  fontWeight="700"
                  px={pxToRem(12)}
                  py={pxToRem(4)}
                  borderRadius={pxToRem(20)}
                  letterSpacing="0.5px"
                  textTransform="uppercase"
                >
                  Recommended
                </Box>

                <Flex align="center" mb={pxToRem(20)}>
                  <Image
                    src="/static/payment/card-tick.svg"
                    alt="Card tick"
                    w={pxToRem(50)}
                    h={pxToRem(50)}
                    mr={pxToRem(14)}
                  />
                  <Box>
                    <Text
                      fontSize={pxToRem(26)}
                      fontWeight="700"
                      color={textColor}
                      mb={pxToRem(4)}
                    >
                      Pro
                    </Text>
                    <Text
                      fontSize={pxToRem(14)}
                      fontWeight="600"
                      color="#1D8AFF"
                      mb={0}
                    >
                      {t('pro-plan-subtitle')}
                    </Text>
                  </Box>
                </Flex>

                <VStack spacing={pxToRem(10)} align="flex-start">
                  {priceDesc.map((feature, index) => (
                    <Flex key={index} align="center">
                      <Flex
                        w={pxToRem(20)}
                        h={pxToRem(20)}
                        bg="#1D8AFF"
                        borderRadius="50%"
                        mr={pxToRem(14)}
                        flexShrink={0}
                        align="center"
                        justify="center"
                      >
                        <Text fontSize={pxToRem(12)} color="white" mb={0} lineHeight="1">
                          &#10003;
                        </Text>
                      </Flex>
                      <Text fontSize={pxToRem(16)} color="#555" mb={0}>
                        {t(feature)}
                      </Text>
                    </Flex>
                  ))}
                </VStack>

                {/* Pricing Cards */}
                <Flex
                  gap={pxToRem(12)}
                  direction={{ base: "column", md: "row" }}
                  align="stretch"
                  mt={pxToRem(24)}
                >
                  {pricingPlans.map((planData, index) => {
                    const isPopular = planData.planType === "monthly";
                    return (
                      <Box
                        key={index}
                        bg={isPopular ? "linear-gradient(135deg, #1D8AFF, #0055DD)" : "white"}
                        border="1px solid"
                        borderColor={isPopular ? "transparent" : "#E2E8F0"}
                        borderRadius={pxToRem(16)}
                        p={pxToRem(16)}
                        flex="1"
                        boxShadow={isPopular
                          ? "0px 6px 20px rgba(29, 138, 255, 0.3)"
                          : "0px 2px 8px rgba(0, 0, 0, 0.05)"
                        }
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        position="relative"
                        transition="all 0.25s ease"
                        _hover={{
                          transform: "translateY(-3px)",
                          boxShadow: isPopular
                            ? "0px 10px 28px rgba(29, 138, 255, 0.4)"
                            : "0px 6px 18px rgba(0, 0, 0, 0.1)",
                        }}
                        cursor="pointer"
                        onClick={() => handlePlanSelect(planData.planType)}
                      >
                        {isPopular && (
                          <Text
                            fontSize={pxToRem(10)}
                            fontWeight="700"
                            color="rgba(255,255,255,0.85)"
                            textTransform="uppercase"
                            letterSpacing="1px"
                            mb={pxToRem(4)}
                          >
                            Most Popular
                          </Text>
                        )}
                        <Text
                          fontSize={pxToRem(36)}
                          fontWeight="700"
                          color={isPopular ? "white" : textColor}
                          mb={0}
                          lineHeight="1.2"
                        >
                          ${planData.price}
                        </Text>
                        <Text
                          fontSize={pxToRem(13)}
                          color={isPopular ? "rgba(255,255,255,0.8)" : "#999"}
                          mb={pxToRem(10)}
                        >
                          /{planData.period}
                        </Text>
                        <Button
                          bg={isPopular ? "white" : "linear-gradient(135deg, #1D8AFF, #0066CC)"}
                          color={isPopular ? "#1D8AFF" : "white"}
                          borderRadius={pxToRem(10)}
                          w="100%"
                          h={pxToRem(36)}
                          fontSize={pxToRem(15)}
                          fontWeight="700"
                          _hover={{
                            opacity: 0.9,
                            transform: "scale(1.02)",
                          }}
                          transition="all 0.2s"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlanSelect(planData.planType);
                          }}
                        >
                          Select
                        </Button>
                      </Box>
                    );
                  })}
                </Flex>
              </Box>
            </Flex>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default GetFullAccessModal;
